import { useState, useRef, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import { connect, useSelector } from "react-redux";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popper,
  Stack,
  Typography,
} from "@mui/material";

// project imports
import MainCard from "../../../../utils/ui-components/MainCard";
import Transitions from "../../../../utils/ui-components/extended/Transitions";

// assets
import SettingsIcon from "@mui/icons-material/Settings";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import { getTimeWelcome, stringAvatar } from "../../../../utils/utils";
import { getLogout, getState } from "../../../../redux/actions/actions";

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = (props: any) => {
  const {
    login,
    getLogout, // remember, Redux Form injects this into our props
  } = props;

  const theme: any = useTheme();
  const customization = useSelector((state: any) => state.customization);
  const authData = useSelector((state: any) => state.auth.authData);
  const user = getState(authData)
  // console.log(user.role)
  const navigate = useNavigate();

  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [open, setOpen] = useState(false);
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef: any = useRef(null);

  const handleLogout = async () => {
    getLogout();
  };

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleListItemClick = (event: any, index: any, route = "") => {
    setSelectedIndex(index);
    handleClose(event);

    if (route && route !== "") {
      navigate(route);
    }
  };
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Chip
        sx={{
          height: "48px",
          alignItems: "center",
          borderRadius: "27px",
          transition: "all .2s ease-in-out",
          borderColor: theme.palette.primary.light,
          backgroundColor: theme.palette.primary.light,
          '&[aria-controls="menu-list-grow"], &:hover': {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            "& svg": {
              stroke: theme.palette.primary.light,
            },
            "& .MuiChip-icon": {
              color: theme.palette.primary.light,
              backgroundColor: theme.palette.secondary.dark,
            },
          },
          "& .MuiChip-label": {
            lineHeight: 0,
          },
        }}
        icon={
          <Avatar
            {...stringAvatar(
              `${getState(login.authData)?.firstName} ${
                getState(login.authData)?.lastName
              }`
            )}
            sx={{
              ...theme.typography.mediumAvatar,
              margin: "8px 0 8px 8px !important",
              cursor: "pointer",
              fontSize:15,
              fontWeight:'bold'
            }}
            ref={anchorRef}
            aria-controls={open ? "menu-list-grow" : undefined}
            aria-haspopup="true"
            color="inherit"
          />
        }
        label={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="icon icon-tabler icon-tabler-settings"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke={theme.palette.primary.main}
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
            <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z"></path>
            <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"></path>
          </svg>
        }
        variant="outlined"
        ref={anchorRef}
        aria-controls={open ? "menu-list-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        color="primary"
      />
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }: any) => (
          <Transitions in={open} {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClose}>
              <MainCard
                border={false}
                elevation={16}
                content={false}
                boxShadow
                shadow={theme.shadows[16]}
              >
                <Box sx={{ p: 2 }}>
                  <Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="h4" color={theme.palette.info.main}>
                        {getTimeWelcome()}
                      </Typography>
                      <Typography
                        component="span"
                        variant="h4"
                        sx={{ fontWeight: 400 }}
                      >
                        {`${getState(login.authData)?.firstName} ${
                          getState(login.authData)?.lastName
                        }`}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2">
                      {user?.role === 0 ? "Owner":
                      user?.role === 1 ? "User" :
                      user?.role === 2 ? "Admin" :
                      user?.role === 3 ? "Ticket Tracker" : ""
                      } Account
                    </Typography>
                  </Stack>
                  <Divider />
                </Box>
                <Box
                  sx={{ p: 2 }}
                  style={{
                    height: "100%",
                    maxHeight: "calc(100vh - 250px)",
                    overflowX: "hidden",
                  }}
                >
                  <List
                    component="nav"
                    sx={{
                      width: "100%",
                      maxWidth: 350,
                      minWidth: 300,
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: "10px",
                      [theme.breakpoints.down("md")]: {
                        minWidth: "100%",
                      },
                      "& .MuiListItemButton-root": {
                        mt: 0.5,
                        borderRadius: `${customization.borderRadius}px`,
                      },
                    }}
                  >
                    <ListItemButton
                      selected={selectedIndex === 0}
                      onClick={(event) =>{}
                        // handleListItemClick(
                        //   event,
                        //   0,
                        //   "/user/account-profile/profile1"
                        // )
                      }
                    >
                      <ListItemIcon>
                        <SettingsIcon
                          sx={{ stroke: 1.5, fontSize: "1.3rem" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            Account Settings
                          </Typography>
                        }
                      />
                    </ListItemButton>
                    {/* <ListItemButton
                      selected={selectedIndex === 1}
                      onClick={(event) =>
                        handleListItemClick(
                          event,
                          1,
                          "/user/social-profile/posts"
                        )
                      }
                    >
                      <ListItemIcon>
                        <PersonOutlineIcon
                          sx={{ stroke: 1.5, fontSize: "1.3rem" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Grid
                            container
                            spacing={1}
                            justifyContent="space-between"
                          >
                            <Grid item>
                              <Typography variant="body2">
                                Social Profile
                              </Typography>
                            </Grid>
                            <Grid item>
                              <Chip
                                label="02"
                                size="small"
                                sx={{
                                  bgcolor: theme.palette.warning.dark,
                                  color: theme.palette.background.default,
                                }}
                              />
                            </Grid>
                          </Grid>
                        }
                      />
                    </ListItemButton> */}
                    <ListItemButton
                      selected={selectedIndex === 4}
                      onClick={handleLogout}
                    >
                      <ListItemIcon>
                        <LogoutIcon sx={{ stroke: 1.5, fontSize: "1.3rem" }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2">Logout</Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </Box>
              </MainCard>
            </ClickAwayListener>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  login: state.auth,
});

export default connect(mapStateToProps, { getLogout })(ProfileSection);
